//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Grid, InputAdornment, InputLabel, TextField, Typography } from '@mui/material'
import React, { ReactElement, useEffect } from 'react'

import {
  commaDecStringToErc20Wei,
  erc20WeiToCommaDecString,
  erc20WeiToErc20DecString,
  erc20WeiToNumber,
} from '@shared/conversions'
import { PoolAccountCTO, PoolOverviewCTO } from '@shared/sharedTypes'
import ButtonAction from '@react/components/elements/ButtonAction'

import { useOtDispatch } from '@react/redux/store'
import { requestEarlyRedeemTx } from '@react/signedTxs/requestEarlyRedeemTx'
import { closeForm } from '@react/UseModalForm'
import { logOT } from '@shared/loggingShared'
import { textFieldValueToBigNumberWei } from '@react/utils/textFieldsUtils'

export default function LenderRequestEarlyWithdrawForm(props: {
  poolOverviewCTO: PoolOverviewCTO
  poolAccountCTO: PoolAccountCTO
}): ReactElement {
  const { poolOverviewCTO, poolAccountCTO } = props

  const dispatch = useOtDispatch()
  const exchangeRateBN = BigInt(poolOverviewCTO?.activeLoanState?.exchangeRateAtDeposit ?? 1)

  const [valuePPT, setValuePPT] = React.useState<string>(erc20WeiToErc20DecString(poolAccountCTO?.maxRedeemRequest))

  const initialUSDCBN = (BigInt(poolAccountCTO?.maxRedeemRequest) * exchangeRateBN) / BigInt(1e18)

  const initialUSDC = Math.round(Number(initialUSDCBN) / 1e4) / 100

  const [valuePrincipal, setValuePrincipal] = React.useState<number>(initialUSDC)
  const [errorPPT, setErrorPPT] = React.useState<string | undefined>(undefined)

  const handleWithdrawRequest = (): void => {
    logOT.debug('LenderRequestWithdrawForm handleWithdrawRequest', valuePPT)
    dispatch(
      requestEarlyRedeemTx({
        poolAddr: poolOverviewCTO.poolAddr,
        earlyWithdrawShares: commaDecStringToErc20Wei(valuePPT),
      })
    )
  }

  const errorCheck = (ppt: bigint): void => {
    logOT.debug('LenderRequestWithdrawForm SHARES errorCheck', ppt)

    if (ppt > BigInt(poolAccountCTO?.maxRedeemRequest)) {
      logOT.debug('LenderRequestEarlyWithdrawForm Error ', ppt, erc20WeiToNumber(poolAccountCTO?.maxRedeemRequest))
      setErrorPPT(
        `Amount exceeds max redemption request of ${erc20WeiToCommaDecString(poolAccountCTO?.maxRedeemRequest)} ${
          poolOverviewCTO.symbol
        }`
      )
    } else {
      setErrorPPT(undefined)
    }
  }

  useEffect(() => {
    logOT.debug('LenderRequestWithdrawForm valuePPT ', valuePPT)

    setValuePrincipal((Number(valuePPT) / 1e9) * (Number(exchangeRateBN) / 1e9))
  }, [valuePPT])

  const handleOnChangePPT = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setValuePPT(event.target.value)
    const ppt = textFieldValueToBigNumberWei(event)
    errorCheck(ppt)
  }
  const disableButton = !!errorPPT
  logOT.debug('LenderRequestWithdrawForm errorUSDC', disableButton, errorPPT)

  return (
    <>
      <Box py={2} px={2}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Typography variant="body" margin="dense" sx={{ marginBottom: 4 }}>
              For Early Withdrawal Requests, your loan principal will be reduced by applying the Early Withdrawal Fee to
              the principal to be terminated early. You will receive the net sale proceeds of the loan collateral
              proportionate to the amount of your loan to be terminated early.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="bodyBold" align="center" margin="dense">
              {`Early Withdrawal Request ${erc20WeiToCommaDecString(poolAccountCTO?.maxRedeemRequest)} ${
                poolOverviewCTO.symbol
              } available to Redeem Early`}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="bodyBold" align="center" margin="dense">
              {`Loan Principal to be Terminated Early: ${valuePrincipal.toLocaleString(navigator.language)} ${poolOverviewCTO.liquidityTokenSymbol}`}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <InputLabel htmlFor="ppt">Withdraw Request ({poolOverviewCTO.symbol})</InputLabel>
            <TextField
              id="ppt"
              InputProps={{
                endAdornment: <InputAdornment position="start">{poolOverviewCTO.symbol}</InputAdornment>,
              }}
              inputProps={{
                'data-testid': 'ppt',
              }}
              sx={{ width: '300px' }}
              variant="outlined"
              placeholder="Enter Withdrawal Amount"
              value={valuePPT}
              type={'tel'}
              onChange={handleOnChangePPT}
              error={!!errorPPT}
              helperText={errorPPT}
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <ButtonAction
              data-testid={'WithdrawRequest'}
              disabled={disableButton}
              sx={{ marginRight: '16px' }}
              variant={'primary'}
              onClick={handleWithdrawRequest}
            >
              Withdraw Request
            </ButtonAction>

            <ButtonAction variant={'tertiary'} onClick={closeForm}>
              Cancel
            </ButtonAction>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}
